import { writable, type Writable } from 'svelte/store';
import i18n from 'sveltekit-i18n';

export interface Language {
	code: string;
	text: string;
}

export const LANGUAGE_COOKIE_NAME: string = 'language';
export const supportedLanguages: Language[] = [
	{
		code: 'en',
		text: 'English'
	},
	{
		code: 'de',
		text: 'Deutsch'
	},
	{
		code: 'ru',
		text: 'Русский'
	},
	{
		code: 'tr',
		text: 'Türkçe'
	},
	{
		code: 'es',
		text: 'Español'
	}
];
export const fallbackLanguage: Language = supportedLanguages[0];
export const displayLanguage: Writable<Language> = writable(fallbackLanguage);

// Search for the language that is supported and has the highest ratio in accepted language string that look like this:
// "en-US,en;q=0.9,en-DE;q=0.8,de-DE;q=0.7,de;q=0.6"
export function getBestMatchFromAcceptedLanguages(acceptedLanguagesString: string): Language | undefined {
	const preferences: { language: string; ratio: number }[] = acceptedLanguagesString.split(',').map((element) => {
		const parts: string[] = element.split(';');
		return {
			language: parts[0].includes('-') ? parts[0].substring(0, parts[0].indexOf('-')) : parts[0],
			ratio: parts.length > 1 ? parseFloat(parts[1]) : Number.MAX_VALUE
		};
	});

	let bestLanguageMatch: Language | undefined = undefined;
	let bestRatio: number = 0.0;

	for (const preference of preferences) {
		const foundSupportedLanguage: Language | undefined = supportedLanguages.find((supportedLanguage) => supportedLanguage.code === preference.language);

		if (!foundSupportedLanguage) {
			continue;
		}

		if (preference.ratio > bestRatio) {
			bestRatio = preference.ratio;
			bestLanguageMatch = foundSupportedLanguage;
		}
	}

	return bestLanguageMatch;
}

export const { t, loadTranslations } = new i18n({
	fallbackLocale: fallbackLanguage.code,
	loaders: supportedLanguages.map((supportedLanguage) => {
		return {
			locale: supportedLanguage.code,
			key: '',
			loader: async () => (await import(`$lib/i18n/translations/${supportedLanguage.code}.json`)).default
		};
	})
});

export function humanizeDate(dateObject: Date, language: Language): string {
	return dateObject.toLocaleDateString(language.code, {
		year: 'numeric', // Numeric year
		month: 'long', // Full month name
		day: 'numeric' // Day of the month
	});
}

export function humanizeNumber(number: number, language: Language): string {
	const formatter: Intl.NumberFormat = new Intl.NumberFormat(language.code);
	return formatter.format(number);
}
